import { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';

export type SortDirection = 'asc' | 'desc';

/**
 * A table column heading that sorts by its column: the arrow says which way,
 * and a column not sorted on shows a quiet two-way arrow. Numbers sort
 * descending first — the biggest is what a reader looks for.
 */
export function SortHeader<K extends string>({
    column,
    sort,
    onSort,
    children,
    align = 'start',
    className,
}: {
    column: K;
    sort: { key: K; dir: SortDirection };
    onSort: (next: { key: K; dir: SortDirection }) => void;
    children: ReactNode;
    align?: 'start' | 'end';
    className?: string;
}) {
    const active = sort.key === column;
    const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
    return (
        <button
            type="button"
            onClick={() => onSort({ key: column, dir: active && sort.dir === 'desc' ? 'asc' : 'desc' })}
            className={cn(
                'inline-flex items-center gap-1 rounded-md text-xs font-medium whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                align === 'end' && 'flex-row-reverse',
                active && 'text-foreground',
                className,
            )}
            aria-pressed={active}
        >
            {children}
            <Icon className={cn('size-3', !active && 'opacity-50')} aria-hidden />
        </button>
    );
}

/** Sort rows by a numeric or text field; nulls (withheld values) always last. */
export function sortRows<T>(rows: T[], key: keyof T | ((row: T) => unknown), dir: SortDirection): T[] {
    const get = typeof key === 'function' ? key : (row: T) => row[key];
    return [...rows].sort((a, b) => {
        const va = get(a) as any;
        const vb = get(b) as any;
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
        return dir === 'asc' ? cmp : -cmp;
    });
}
